<script setup>
import { ref, computed, watch } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
});

const isLoading = ref(false);
const balanceData = ref(null);
const credits = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);

const currentBalance = computed(() => balanceData.value?.balance || 0);
const formattedBalance = computed(() => formatCurrency(currentBalance.value));

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function amountClass(value) {
  if (value < 0) return 'text-n-ruby-9';
  if (value > 0) return 'text-n-jade-9';
  return 'text-n-slate-11';
}

async function fetchBalance() {
  try {
    const response = await InstagramMediaReportsAPI.getMonitorCreditsBalance({
      inboxId: props.selectedInbox?.id,
    });
    balanceData.value = response.data;
  } catch {
    // silently fail
  }
}

async function fetchCredits(page = 1) {
  isLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getMonitorCredits({
      inboxId: props.selectedInbox?.id,
      page,
    });
    credits.value = response.data.records || [];
    totalPages.value = response.data.total_pages || 1;
    currentPage.value = page;
  } catch {
    credits.value = [];
  } finally {
    isLoading.value = false;
  }
}

function goToPage(page) {
  if (page >= 1 && page <= totalPages.value) {
    fetchCredits(page);
  }
}

watch(
  () => props.selectedInbox?.id,
  () => {
    if (props.selectedInbox?.id) {
      fetchBalance();
      fetchCredits(1);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Balance Card -->
    <div
      class="flex items-center justify-between rounded-xl border border-n-weak bg-n-alpha-black2 p-6"
    >
      <div>
        <p class="text-sm font-medium text-n-slate-11">Saldo atual</p>
        <p class="mt-1 text-3xl font-bold text-n-slate-12">
          {{ formattedBalance }}
        </p>
      </div>
      <div
        class="flex h-12 w-12 items-center justify-center rounded-full bg-n-blue-3"
      >
        <fluent-icon icon="credit-card-person" size="24" class="text-n-blue-11" />
      </div>
    </div>

    <!-- Extrato Table -->
    <div class="rounded-xl border border-n-weak bg-n-alpha-black2 p-4">
      <h3 class="mb-4 text-base font-semibold text-n-slate-12">
        Extrato detalhado
      </h3>

      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <Spinner />
      </div>

      <div v-else-if="credits.length === 0" class="py-12 text-center">
        <p class="text-sm text-n-slate-10">Nenhum movimento encontrado</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak text-left text-xs text-n-slate-10">
              <th class="px-3 py-2">Data</th>
              <th class="px-3 py-2">Movimento</th>
              <th class="px-3 py-2">Descri&ccedil;&atilde;o</th>
              <th class="px-3 py-2 text-right">PopsisRedesapi (R$)</th>
              <th class="px-3 py-2 text-right">ElisiA (R$)</th>
              <th class="px-3 py-2 text-right">Lan&ccedil;amento (R$)</th>
              <th class="px-3 py-2 text-right">Saldo (R$)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in credits"
              :key="entry.id"
              class="border-b border-n-weak last:border-0"
            >
              <td class="whitespace-nowrap px-3 py-2 text-n-slate-11">
                {{ formatDate(entry.movement_at) }}
              </td>
              <td class="px-3 py-2 text-n-slate-12">
                {{ entry.movement_type }}
              </td>
              <td class="max-w-[200px] truncate px-3 py-2 text-n-slate-11">
                {{ entry.description }}
              </td>
              <td class="px-3 py-2 text-right text-n-slate-11">
                {{ formatCurrency(entry.popsis_cost) }}
              </td>
              <td class="px-3 py-2 text-right text-n-slate-11">
                {{ formatCurrency(entry.elisia_cost) }}
              </td>
              <td
                class="px-3 py-2 text-right font-medium"
                :class="amountClass(entry.amount)"
              >
                {{ formatCurrency(entry.amount) }}
              </td>
              <td class="px-3 py-2 text-right font-medium text-n-slate-12">
                {{ formatCurrency(entry.balance) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div
        v-if="totalPages > 1"
        class="mt-4 flex items-center justify-center gap-2"
      >
        <button
          class="rounded-lg px-3 py-1 text-sm text-n-slate-11 hover:bg-n-alpha-black2 disabled:opacity-40"
          :disabled="currentPage <= 1"
          @click="goToPage(currentPage - 1)"
        >
          Anterior
        </button>
        <span class="text-sm text-n-slate-10">
          {{ currentPage }} / {{ totalPages }}
        </span>
        <button
          class="rounded-lg px-3 py-1 text-sm text-n-slate-11 hover:bg-n-alpha-black2 disabled:opacity-40"
          :disabled="currentPage >= totalPages"
          @click="goToPage(currentPage + 1)"
        >
          Pr&oacute;ximo
        </button>
      </div>
    </div>
  </div>
</template>
